import {
  Button,
  Col,
  DatePicker,
  Divider,
  Empty,
  Flex,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import CustomLineChart from "../../common/CustomLineChart";
import {
  DownloadOutlined,
  SearchOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import * as _ from "@antv/util";
import {
  useContext,
  useDebugValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import useFetch from "../../../hooks/useFetch";
import { IndexContext } from "../../../context/IndexContext";

const { Title, Text } = Typography;

export default function TrendingTab({
  themeToken,
  deviceID,
  deviceData,
  siteCommissioningDate,
}) {
  const { notificationApi } = useContext(IndexContext);
  const [parameterSelection, setParameterSelection] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState([]);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const refreshController = useRef(new AbortController());
  const downloadController = useRef(new AbortController());
  const [downloading, setDownloading] = useState(false);
  const [trendData, setTrendData] = useState([]);
  const [startDate, setStartDate] = useState(
    dayjs(
      Math.max(
        dayjs().hour(0).minute(0).second(0).add(-14, "day").valueOf(),
        dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
      )
    )
  );
  const [endDate, setEndDate] = useState(
    dayjs().hour(0).minute(0).second(0).add(1, "day")
  );
  const [fetchWrapper] = useFetch();

  function getDeviceParameterSelection() {
    fetchWrapper({
      method: "GET",
      endpoint_url: "device_monitoring/getDeviceParameterSelection",
      params: {
        DeviceID: deviceID,
      },
      onSuccess: (response) => {
        setParameterSelection(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
      },
      onFetchError: (error) => {
        notificationApi.error({
          message: "Server Error",
          descripton: error.message,
        });
      },
    });
  }

  function getParameterTrend() {
    setRefreshLoading(true);
    refreshController.current.abort();
    refreshController.current = new AbortController();

    fetchWrapper({
      abort_controller: refreshController.current,
      method: "POST",
      endpoint_url: "device_monitoring/getParameterTrend",
      body: {
        device_id: deviceID,
        parameter_keys: selectedParameter,
        start_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      onSuccess: (response) => {
        setTrendData(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
      },
      onFetchError: (error) => {
        notificationApi.error({
          message: "Server Error",
          descripton: error.message,
        });
      },
      onFinish: () => {
        setRefreshLoading(false);
      },
    });
  }

  function downloadParameterTrend() {
    setDownloading(true);
    downloadController.current.abort();
    downloadController.current = new AbortController();

    fetchWrapper({
      abort_controller: downloadController.current,
      method: "POST",
      endpoint_url: "device_monitoring/downloadParameterTrend",
      body: {
        device_id: deviceID,
        parameter_keys: selectedParameter,
        start_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      json_output: false,
      onSuccess: (response) => {
        // returned a json response.
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${deviceData?.name ?? "Unknown"}_${dayjs().format(
              "YYYYMMDD_HHmmss"
            )}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
      },
      onFetchError: (error) => {
        notificationApi.error({
          message: "Server Error",
          descripton: error.message,
        });
      },
      onFinish: () => {
        setDownloading(false);
      },
    });
  }

  useEffect(() => {
    getDeviceParameterSelection();
    return () => {
      refreshController.current.abort();
      downloadController.current.abort();
    };
  }, []);

  return (
    <div
      style={{
        maxHeight: "80vh",
        height: "calc(70vh - 30px)",
        // overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Space style={{ width: "100%" }} direction="vertical">
        <Text>Parameter :</Text>
        <Select
          style={{ width: "100%", marginBottom: 12 }}
          placeholder="Select Parameter"
          options={parameterSelection}
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxCount={8}
          value={selectedParameter}
          onChange={(value) => setSelectedParameter(value)}
          optionRender={(option) => option.data.name}
          tagRender={({ label, onClose, closable }) => (
            <Tag
              color={themeToken.colorPrimary}
              closable={closable}
              onClose={onClose}
              style={{
                marginRight: 3,
              }}
            >
              {label}
            </Tag>
          )}
          maxTagCount="responsive"
        />
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={10}>
            <Space.Compact block>
              <Button disabled>Start</Button>
              <DatePicker
                style={{ width: "100%" }}
                showTime
                showHour
                showMinute
                showSecond={false}
                minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
                maxDate={endDate}
                value={startDate}
                placement="topRight"
                allowClear={false}
                needConfirm={false}
                onChange={(value) => {
                  setStartDate(value);
                }}
              />
              <Button disabled>End</Button>
              <DatePicker
                style={{ width: "100%" }}
                showTime
                showHour
                showMinute
                showSecond={false}
                minDate={startDate}
                maxDate={dayjs()}
                value={endDate}
                placement="topLeft"
                allowClear={false}
                needConfirm={false}
                onChange={(value) => {
                  setEndDate(value);
                }}
              />
              <Button
                icon={<UndoOutlined />}
                onClick={() => {
                  setStartDate(
                    dayjs(
                      Math.max(
                        dayjs()
                          .hour(0)
                          .minute(0)
                          .second(0)
                          .add(-14, "day")
                          .valueOf(),
                        dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
                      )
                    )
                  );
                  setEndDate(dayjs().hour(0).minute(0).second(0).add(1, "day"));
                }}
              ></Button>
            </Space.Compact>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Flex gap={12}>
              <Button
                icon={<SearchOutlined />}
                onClick={() => getParameterTrend()}
                type="primary"
                loading={refreshLoading}
              >
                Search
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={() => downloadParameterTrend()}
              >
                Download
              </Button>
            </Flex>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Spin spinning={refreshLoading}>
              <div style={{ height: 450 }}>
                {trendData?.length != 0 ? (
                  <CustomLineChart
                    height={450}
                    data={trendData}
                    themeToken={themeToken}
                  ></CustomLineChart>
                ) : (
                  <>
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <Empty />
                  </>
                )}
              </div>
            </Spin>
          </Col>
        </Row>
      </Space>
    </div>
  );
}
